<template>
  <div class="sales-search-filters">
    <b-button
      variant="outline-secondary"
      size="sm"
      class="d-inline-flex align-items-center"
      @click="showModal = true"
    >
      <feather-icon
        icon="FilterIcon"
        size="16"
        class="mr-50"
      />
      <span>Filters</span>
      <b-badge
        v-if="hasActiveFilters"
        variant="primary"
        class="ml-50"
      >
        Active
      </b-badge>
    </b-button>

    <b-modal
      id="sales-search-filters-modal"
      v-model="showModal"
      title="Filters"
      size="md"
      hide-footer
    >
      <b-form>
        <b-row>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              label="Date from"
              label-for="sales-filter-date-from"
            >
              <b-form-datepicker
                id="sales-filter-date-from"
                :value="filterDateFrom"
                locale="en"
                size="sm"
                placeholder="From"
                reset-button
                class="w-100"
                :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }"
                @input="$emit('update:filterDateFrom', $event)"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              label="Date to"
              label-for="sales-filter-date-to"
            >
              <b-form-datepicker
                id="sales-filter-date-to"
                :value="filterDateTo"
                locale="en"
                size="sm"
                placeholder="To"
                reset-button
                class="w-100"
                :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }"
                @input="$emit('update:filterDateTo', $event)"
              />
            </b-form-group>
          </b-col>
          <b-col
            v-if="companyPurpose !== 'rent'"
            cols="12"
          >
            <b-form-group
              label="Category"
              label-for="sales-filter-category"
            >
              <ImageTextSelect
                id="sales-filter-category"
                :model-value="filterCategoryId"
                :options="categoryFilterOptions"
                @update:model-value="$emit('update:filterCategoryId', $event)"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              label="Total from"
              label-for="sales-filter-total-from"
            >
              <b-form-input
                id="sales-filter-total-from"
                :value="filterTotalFrom"
                placeholder="Total from"
                type="number"
                min="0"
                step="0.01"
                size="sm"
                @input="$emit('update:filterTotalFrom', normalizeInput($event))"
                @keypress="blockDecimalFilterKey"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              label="Total to"
              label-for="sales-filter-total-to"
            >
              <b-form-input
                id="sales-filter-total-to"
                :value="filterTotalTo"
                placeholder="Total to"
                type="number"
                min="0"
                step="0.01"
                size="sm"
                @input="$emit('update:filterTotalTo', normalizeInput($event))"
                @keypress="blockDecimalFilterKey"
              />
            </b-form-group>
          </b-col>
          <b-col
            v-if="companyPurpose !== 'rent' && canSeeAllSupervisorsOrders"
            cols="12"
          >
            <b-form-group
              label="Supervisor"
              label-for="sales-filter-supervisor"
            >
              <ImageTextSelect
                id="sales-filter-supervisor"
                :model-value="filterSupervisorId"
                :options="supervisorFilterOptions"
                :fallback-image="supervisorPictureFallback"
                @update:model-value="$emit('update:filterSupervisorId', $event)"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group
              label="Search by keyword"
              label-for="sales-filter-search"
            >
              <BarcodeTargetField
                hint-key="orders-filters-search"
                :active="showModal"
              >
                <b-form-input
                  id="sales-filter-search"
                  :value="searchTerm"
                  placeholder="keyword"
                  type="text"
                  size="sm"
                  class="barcode-target"
                  @input="$emit('update:searchTerm', normalizeInput($event))"
                />
              </BarcodeTargetField>
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>

      <div class="d-flex justify-content-between align-items-center mt-1">
        <div class="sales-search-filters__status text-muted small d-flex align-items-center">
          <b-spinner
            v-if="isFilterLoading"
            small
            label="Loading..."
          />
          <span v-else-if="statusMessage">{{ statusMessage }}</span>
        </div>
        <div class="d-flex align-items-center">
          <b-button
            variant="outline-primary"
            size="sm"
            class="mr-1"
            :disabled="!hasActiveFilters"
            @click="$emit('reset')"
          >
            Reset
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            @click="showModal = false"
          >
            Close
          </b-button>
        </div>
      </div>

    </b-modal>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCol,
  BForm,
  BFormDatepicker,
  BFormGroup,
  BFormInput,
  BModal,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import ImageTextSelect from '@/views/Components/Elements/ImageTextSelect.vue'
import BarcodeTargetField from '@/views/Components/Elements/BarcodeTargetField.vue'

export default {
  name: 'SalesSearchFilters',
  components: {
    BBadge,
    BButton,
    BCol,
    BForm,
    BFormDatepicker,
    BFormGroup,
    BFormInput,
    BModal,
    BRow,
    BSpinner,
    ImageTextSelect,
    BarcodeTargetField,
  },
  props: {
    filterDateFrom: {
      default: null,
    },
    filterDateTo: {
      default: null,
    },
    filterCategoryId: {
      default: null,
    },
    filterTotalFrom: {
      type: [String, Number],
      default: '',
    },
    filterTotalTo: {
      type: [String, Number],
      default: '',
    },
    filterSupervisorId: {
      type: [String, Number],
      default: '',
    },
    searchTerm: {
      type: String,
      default: '',
    },
    categoryFilterOptions: {
      type: Array,
      default: () => [],
    },
    supervisorFilterOptions: {
      type: Array,
      default: () => [],
    },
    canSeeAllSupervisorsOrders: {
      type: Boolean,
      default: false,
    },
    companyPurpose: {
      type: String,
      default: '',
    },
    supervisorPictureFallback: {
      type: String,
      default: '',
    },
    hasActiveFilters: {
      type: Boolean,
      default: false,
    },
    filteredCount: {
      type: Number,
      default: 0,
    },
  },
  emits: [
    'update:filterDateFrom',
    'update:filterDateTo',
    'update:filterCategoryId',
    'update:filterTotalFrom',
    'update:filterTotalTo',
    'update:filterSupervisorId',
    'update:searchTerm',
    'reset',
  ],
  data() {
    return {
      showModal: false,
      isFilterLoading: false,
      showResultStatus: false,
      filterFeedbackTimer: null,
    }
  },
  computed: {
    entityLabel() {
      return this.companyPurpose === 'rent' ? 'booking' : 'order'
    },
    filterSignature() {
      return [
        this.filterDateFrom,
        this.filterDateTo,
        this.filterCategoryId,
        this.filterTotalFrom,
        this.filterTotalTo,
        this.filterSupervisorId,
        this.searchTerm,
        this.hasActiveFilters,
        this.filteredCount,
      ].join('|')
    },
    statusMessage() {
      if (!this.showResultStatus || !this.hasActiveFilters) {
        return ''
      }

      const count = Number(this.filteredCount) || 0
      if (count === 1) {
        return `Found one ${this.entityLabel}`
      }

      return `Found ${count} ${this.entityLabel}s`
    },
  },
  watch: {
    filterSignature() {
      if (!this.hasActiveFilters) {
        this.clearFilterFeedback()
        return
      }
      this.scheduleFilterFeedback()
    },
  },
  beforeDestroy() {
    this.clearFilterFeedbackTimer()
  },
  methods: {
    normalizeInput(value) {
      return value && typeof value === 'object' && value.target
        ? value.target.value
        : value
    },
    blockDecimalFilterKey(event) {
      if (event.ctrlKey || event.metaKey || event.altKey) {
        return
      }

      const { key } = event
      if (
        key === 'Backspace'
        || key === 'Delete'
        || key === 'Tab'
        || key === 'Enter'
        || key === 'Escape'
        || key.startsWith('Arrow')
      ) {
        return
      }

      const input = event.target
      if (key === '.' && input && !String(input.value).includes('.')) {
        return
      }

      if (key.length === 1 && /\d/.test(key)) {
        return
      }

      event.preventDefault()
    },
    clearFilterFeedbackTimer() {
      if (this.filterFeedbackTimer) {
        clearTimeout(this.filterFeedbackTimer)
        this.filterFeedbackTimer = null
      }
    },
    clearFilterFeedback() {
      this.clearFilterFeedbackTimer()
      this.isFilterLoading = false
      this.showResultStatus = false
    },
    scheduleFilterFeedback() {
      if (!this.showModal || !this.hasActiveFilters) {
        return
      }

      this.clearFilterFeedbackTimer()
      this.isFilterLoading = true
      this.showResultStatus = false

      this.filterFeedbackTimer = setTimeout(() => {
        this.isFilterLoading = false
        this.showResultStatus = true
        this.filterFeedbackTimer = null
      }, 250)
    },
  },
}
</script>
